// comparePassports.js
export const comparePassports = (passports, selectedPassports, countries, travelerCounts, options = {}) => {
    const {
        region = 'all', // 'all', 'africa', 'asia', 'europe', 'north-america', 'south-america', 'oceania', 'caribbean', 'central-america'
        maxCountries = 5, // 5, 10, 20, 50, 'all'
    } = options;

    console.log('Comparing passports with data:', { passports, selectedPassports, countries, travelerCounts, options });
    
    // Extract passport IDs from selectedPassports if they are objects
    const selectedPassportIds = selectedPassports.map(p => typeof p === 'object' ? p.countryId : p);
    const selectedPassportData = passports.filter(p => selectedPassportIds.includes(p.id));
    console.log('Selected passport data:', selectedPassportData);
    
    const results = countries
        .filter(country => region === 'all' || country.region === region)
        .map(country => {
            // Get passports that already have no visa required
            const noVisa = selectedPassportData.filter(p => 
                country.noVisaRequired?.includes(p.id)
            );

            // Add passports from the same country as the destination
            const sameCountryPassports = selectedPassportData.filter(p => 
                p.id === country.id
            );
            
            // Combine both lists, ensuring no duplicates
            const allNoVisa = [...new Set([...noVisa, ...sameCountryPassports])];

            const visaOnArrival = selectedPassportData.filter(p => 
                country.visaOnArrival?.includes(p.id)
            );
            const eVisa = selectedPassportData.filter(p => 
                country.eVisaRequired?.includes(p.id)
            );
            const paperVisa = selectedPassportData.filter(p => 
                country.paperVisaRequired?.includes(p.id)
            );

            const noVisaCount = allNoVisa.length;
            const visaOnArrivalCount = visaOnArrival.length;
            const eVisaCount = eVisa.length;
            const paperVisaCount = paperVisa.length;

            // Calculate weighted scores based on passport weights and traveler counts
            const noVisaWeight = allNoVisa.reduce((sum, p) => {
                const travelerCount = travelerCounts[p.id] || 1;
                const weight = (p.internalWeight || 1) * travelerCount;
                console.log(`No visa weight for ${p.country}: ${weight} (${p.internalWeight || 1} × ${travelerCount} travelers)`);
                return sum + weight;
            }, 0);

            const visaOnArrivalWeight = visaOnArrival.reduce((sum, p) => {
                const travelerCount = travelerCounts[p.id] || 1;
                const weight = (p.internalWeight || 1) * travelerCount;
                console.log(`Visa on arrival weight for ${p.country}: ${weight} (${p.internalWeight || 1} × ${travelerCount} travelers)`);
                return sum + weight;
            }, 0);

            const eVisaWeight = eVisa.reduce((sum, p) => {
                const travelerCount = travelerCounts[p.id] || 1;
                const weight = (p.internalWeight || 1) * travelerCount;
                console.log(`eVisa weight for ${p.country}: ${weight} (${p.internalWeight || 1} × ${travelerCount} travelers)`);
                return sum + weight;
            }, 0);

            const paperVisaWeight = paperVisa.reduce((sum, p) => {
                const travelerCount = travelerCounts[p.id] || 1;
                const weight = (p.internalWeight || 1) * travelerCount;
                console.log(`Paper visa weight for ${p.country}: ${weight} (${p.internalWeight || 1} × ${travelerCount} travelers)`);
                return sum + weight;
            }, 0);

            const accessibilityScore = 
                (noVisaWeight * 1000) + 
                (visaOnArrivalWeight * 100) + 
                (eVisaWeight * 10) + 
                (paperVisaWeight * 1);

            if (noVisaCount + visaOnArrivalCount + eVisaCount + paperVisaCount > 0) {
                return {
                    country: country.country,
                    id: country.id,
                    region: country.region,
                    noVisa: allNoVisa.map(p => ({
                        country: p.country,
                        weight: (p.internalWeight || 1) * (travelerCounts[p.id] || 1)
                    })),
                    visaOnArrival: visaOnArrival.map(p => ({
                        country: p.country,
                        weight: (p.internalWeight || 1) * (travelerCounts[p.id] || 1)
                    })),
                    eVisa: eVisa.map(p => ({
                        country: p.country,
                        weight: (p.internalWeight || 1) * (travelerCounts[p.id] || 1)
                    })),
                    paperVisa: paperVisa.map(p => ({
                        country: p.country,
                        weight: (p.internalWeight || 1) * (travelerCounts[p.id] || 1)
                    })),
                    totalPassports: selectedPassportIds.length,
                    noVisaCount,
                    visaOnArrivalCount,
                    eVisaCount,
                    paperVisaCount,
                    noVisaWeight,
                    visaOnArrivalWeight,
                    eVisaWeight,
                    paperVisaWeight,
                    accessibilityScore
                };
            }
            return null;
        }).filter(Boolean);

    console.log('Final comparison results:', results);
    const sortedResults = results.sort((a, b) => b.accessibilityScore - a.accessibilityScore);
    
    // Apply maxCountries filter
    if (maxCountries !== 'all') {
        return sortedResults.slice(0, maxCountries);
    }
    return sortedResults;
};